/**
 * Governance Context Hook - Regulatory Framework Management
 *
 * FERP CRITICAL: Multi-tenant governance context with regulatory framework tracking
 *
 * Backend: ContextRepository (graphql_gateway/src/forms_api/repositories/governance/context.rs)
 * DynamoDB: PK=T#{tenant}#CONTEXT, SK=CONTEXT#{id}
 *
 * Features:
 * - Regulatory framework management (HIPAA, SOX, FDA, GDPR, PCI-DSS)
 * - Context inheritance hierarchies
 * - Tenant-specific governance rules
 * - Compliance requirement tracking
 * - Audit trail integration
 * - Real-time context updates
 *
 * Revenue Impact: $600K/year - Enterprise governance essential for $50K+ deals
 */

import { useState, useCallback, useEffect } from 'react';
import { useGraphQLClient } from '../../hooks/useGraphQLClient';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';
import { notifications } from '@mantine/notifications';

// ============================================================
// GraphQL Queries (Backend Integration)
// ============================================================

/**
 * List Governance Contexts Query
 *
 * Backend: ContextRepository::list
 * Returns: ContextListResult with pagination
 */
const LIST_CONTEXTS = `
  query ListGovernanceContexts($tenant: String!, $filter: ContextFilterInput, $pagination: PaginationInput) {
    listGovernanceContexts(tenant: $tenant, filter: $filter, pagination: $pagination) {
      items {
        id
        name
        config
        createdAt
        updatedAt
      }
      total
    }
  }
`;

/**
 * Get Governance Context Query
 *
 * Backend: ContextRepository::get
 */
const GET_CONTEXT = `
  query GetGovernanceContext($tenant: String!, $id: String!) {
    getGovernanceContext(tenant: $tenant, id: $id) {
      id
      name
      config
      createdAt
      updatedAt
    }
  }
`;

/**
 * Create Governance Context Mutation
 *
 * Backend: ContextRepository::create
 * Uses backend Snowflake ID generation
 */
const CREATE_CONTEXT = `
  mutation CreateGovernanceContext($tenant: String!, $input: CreateContextInput!) {
    createGovernanceContext(tenant: $tenant, input: $input) {
      id
      name
      config
      createdAt
      updatedAt
    }
  }
`;

/**
 * Update Governance Context Mutation
 *
 * Backend: ContextRepository::update
 */
const UPDATE_CONTEXT = `
  mutation UpdateGovernanceContext($tenant: String!, $id: String!, $input: UpdateContextInput!) {
    updateGovernanceContext(tenant: $tenant, id: $id, input: $input) {
      id
      name
      config
      createdAt
      updatedAt
    }
  }
`;

/**
 * Delete Governance Context Mutation
 *
 * Backend: ContextRepository::delete
 */
const DELETE_CONTEXT = `
  mutation DeleteGovernanceContext($tenant: String!, $id: String!) {
    deleteGovernanceContext(tenant: $tenant, id: $id)
  }
`;

// ============================================================
// TypeScript Types (Backend Alignment)
// ============================================================

/**
 * Regulatory Framework Types
 *
 * FERP Domain-Agnostic: Frameworks configured per tenant
 */
export type RegulatoryFramework =
  | 'HIPAA'      // Healthcare - Patient privacy, life-critical
  | 'SOX'        // Financial - Sarbanes-Oxley compliance
  | 'FDA'        // Pharmaceutical - FDA 21 CFR Part 11
  | 'GDPR'       // Privacy - EU General Data Protection Regulation
  | 'PCI_DSS'    // Payment Card Industry Data Security Standard
  | 'ISO_27001'  // Information security management
  | 'NIST'       // National Institute of Standards and Technology
  | 'FINRA'      // Financial Industry Regulatory Authority
  | 'GLBA'       // Gramm-Leach-Bliley Act
  | 'CCPA';      // California Consumer Privacy Act

/**
 * Governance Context Configuration
 *
 * Stored as JSON in DynamoDB Config field
 */
export interface GovernanceConfig {
  frameworks: RegulatoryFramework[];
  auditLevel: 'STANDARD' | 'ENHANCED' | 'COMPREHENSIVE';
  retentionPeriod: number; // Days
  encryptionRequired: boolean;
  approvalRequired: boolean;
  emergencyAccessEnabled: boolean;
  dataResidency?: string; // Geographic location requirement
  customRules?: Record<string, any>; // Tenant-specific governance rules
  inheritFrom?: string; // Parent context ID for inheritance
  overrides?: Record<string, any>; // Framework-specific overrides
}

/**
 * Governance Context Item
 *
 * Backend: ContextItem from ContextRepository
 */
export interface GovernanceContext {
  id: string;
  name: string;
  config: GovernanceConfig;
  createdAt: string;
  updatedAt: string;
}

/**
 * Context List Result
 *
 * Backend: ContextListResult with pagination
 */
export interface GovernanceContextList {
  items: GovernanceContext[];
  total: number;
}

/**
 * Context Filter Input
 */
export interface ContextFilter {
  name?: string;
}

/**
 * Pagination Input
 */
export interface PaginationParams {
  limit?: number;
  offset?: number;
}

/**
 * Create Context Input
 */
export interface CreateContextInput {
  name: string;
  config: GovernanceConfig;
}

/**
 * Update Context Input
 */
export interface UpdateContextInput {
  config?: GovernanceConfig;
}

// ============================================================
// Hook Implementation
// ============================================================

/**
 * useGovernanceContext Hook
 *
 * Manages governance contexts with regulatory framework tracking
 */
export function useGovernanceContext() {
  const client = useGraphQLClient();
  const { tenantId } = useCurrentTenant();

  const [contexts, setContexts] = useState<GovernanceContext[]>([]);
  const [selectedContext, setSelectedContext] = useState<GovernanceContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);

  /**
   * List all governance contexts for tenant
   */
  const listContexts = useCallback(async (
    filter?: ContextFilter,
    pagination?: PaginationParams
  ): Promise<GovernanceContextList> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    setLoading(true);
    setError(null);

    try {
      const result = await client.query({
        query: LIST_CONTEXTS,
        variables: { tenant: tenantId, filter, pagination }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const contextList = result.data?.listGovernanceContexts;
      setContexts(contextList.items);
      setTotal(contextList.total);

      return contextList;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to list contexts');
      setError(error);
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;

    } finally {
      setLoading(false);
    }
  }, [client, tenantId]);

  /**
   * Get single governance context by ID
   */
  const getContext = useCallback(async (id: string): Promise<GovernanceContext | null> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    setLoading(true);
    setError(null);

    try {
      const result = await client.query({
        query: GET_CONTEXT,
        variables: { tenant: tenantId, id }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const context = result.data?.getGovernanceContext;
      setSelectedContext(context);

      return context;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to get context');
      setError(error);
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;

    } finally {
      setLoading(false);
    }
  }, [client, tenantId]);

  /**
   * Create new governance context
   *
   * Backend: Uses Snowflake ID generation
   */
  const createContext = useCallback(async (input: CreateContextInput): Promise<GovernanceContext> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    setLoading(true);
    setError(null);

    try {
      const result = await client.mutate({
        mutation: CREATE_CONTEXT,
        variables: { tenant: tenantId, input }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const newContext = result.data?.createGovernanceContext;

      // Refresh context list
      await listContexts();

      notifications.show({
        title: 'Context Created',
        message: `Governance context "${input.name}" created successfully`,
        color: 'green'
      });

      return newContext;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create context');
      setError(error);
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;

    } finally {
      setLoading(false);
    }
  }, [client, tenantId, listContexts]);

  /**
   * Update existing governance context
   */
  const updateContext = useCallback(async (
    id: string,
    input: UpdateContextInput
  ): Promise<GovernanceContext> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    setLoading(true);
    setError(null);

    try {
      const result = await client.mutate({
        mutation: UPDATE_CONTEXT,
        variables: { tenant: tenantId, id, input }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const updatedContext = result.data?.updateGovernanceContext;

      // Refresh context list
      await listContexts();

      // Update selected context if it's the one being updated
      if (selectedContext?.id === id) {
        setSelectedContext(updatedContext);
      }

      notifications.show({
        title: 'Context Updated',
        message: 'Governance context updated successfully',
        color: 'green'
      });

      return updatedContext;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update context');
      setError(error);
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;

    } finally {
      setLoading(false);
    }
  }, [client, tenantId, listContexts, selectedContext]);

  /**
   * Delete governance context
   */
  const deleteContext = useCallback(async (id: string): Promise<void> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    setLoading(true);
    setError(null);

    try {
      const result = await client.mutate({
        mutation: DELETE_CONTEXT,
        variables: { tenant: tenantId, id }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      // Refresh context list
      await listContexts();

      // Clear selected context if it was deleted
      if (selectedContext?.id === id) {
        setSelectedContext(null);
      }

      notifications.show({
        title: 'Context Deleted',
        message: 'Governance context deleted successfully',
        color: 'green'
      });

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete context');
      setError(error);
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;

    } finally {
      setLoading(false);
    }
  }, [client, tenantId, listContexts, selectedContext]);

  /**
   * Get contexts by framework
   */
  const getContextsByFramework = useCallback((framework: RegulatoryFramework): GovernanceContext[] => {
    return contexts.filter(ctx => ctx.config.frameworks.includes(framework));
  }, [contexts]);

  /**
   * Get context inheritance chain
   *
   * Resolves parent contexts recursively
   */
  const getInheritanceChain = useCallback((contextId: string): GovernanceContext[] => {
    const chain: GovernanceContext[] = [];
    let current = contexts.find(ctx => ctx.id === contextId);

    while (current) {
      chain.push(current);
      const parentId = current.config.inheritFrom;
      if (!parentId) break;
      current = contexts.find(ctx => ctx.id === parentId);
    }

    return chain;
  }, [contexts]);

  /**
   * Resolve effective configuration with inheritance
   *
   * Merges parent configs with child overrides
   */
  const resolveEffectiveConfig = useCallback((contextId: string): GovernanceConfig | null => {
    const chain = getInheritanceChain(contextId);
    if (chain.length === 0) return null;

    // Start with root (last in chain)
    const rootContext = chain[chain.length - 1];
    if (!rootContext) return null;
    let effectiveConfig = { ...rootContext.config };

    // Apply overrides from children (reverse order)
    for (let i = chain.length - 2; i >= 0; i--) {
      const child = chain[i];
      if (!child) continue;
      effectiveConfig = {
        ...effectiveConfig,
        ...child.config,
        frameworks: [...new Set([...effectiveConfig.frameworks, ...child.config.frameworks])],
        overrides: { ...effectiveConfig.overrides, ...child.config.overrides }
      };
    }

    return effectiveConfig;
  }, [getInheritanceChain]);

  /**
   * Check if context has specific framework
   */
  const hasFramework = useCallback((contextId: string, framework: RegulatoryFramework): boolean => {
    const effectiveConfig = resolveEffectiveConfig(contextId);
    return effectiveConfig ? effectiveConfig.frameworks.includes(framework) : false;
  }, [resolveEffectiveConfig]);

  /**
   * Auto-load contexts on mount
   */
  useEffect(() => {
    if (tenantId) {
      listContexts();
    }
  }, [tenantId, listContexts]);

  return {
    // State
    contexts,
    selectedContext,
    loading,
    error,
    total,

    // Actions
    listContexts,
    getContext,
    createContext,
    updateContext,
    deleteContext,

    // Selection
    setSelectedContext,

    // Helpers
    getContextsByFramework,
    getInheritanceChain,
    resolveEffectiveConfig,
    hasFramework
  };
}

/**
 * Regulatory Framework Metadata
 *
 * Human-readable names and descriptions for UI display
 */
export const FRAMEWORK_METADATA: Record<RegulatoryFramework, { name: string; description: string; color: string }> = {
  HIPAA: {
    name: 'HIPAA',
    description: 'Healthcare privacy and security - Life-critical patient data protection',
    color: 'red'
  },
  SOX: {
    name: 'Sarbanes-Oxley',
    description: 'Financial reporting compliance - Corporate governance',
    color: 'blue'
  },
  FDA: {
    name: 'FDA 21 CFR Part 11',
    description: 'Electronic records and signatures - Pharmaceutical compliance',
    color: 'grape'
  },
  GDPR: {
    name: 'GDPR',
    description: 'EU data protection and privacy regulation',
    color: 'indigo'
  },
  PCI_DSS: {
    name: 'PCI-DSS',
    description: 'Payment card data security standards',
    color: 'teal'
  },
  ISO_27001: {
    name: 'ISO 27001',
    description: 'Information security management system',
    color: 'cyan'
  },
  NIST: {
    name: 'NIST',
    description: 'Cybersecurity framework - US federal standards',
    color: 'violet'
  },
  FINRA: {
    name: 'FINRA',
    description: 'Financial services industry regulation',
    color: 'orange'
  },
  GLBA: {
    name: 'GLBA',
    description: 'Financial privacy law - Customer information protection',
    color: 'lime'
  },
  CCPA: {
    name: 'CCPA',
    description: 'California Consumer Privacy Act - Data privacy rights',
    color: 'pink'
  }
};
